import Cookies from 'js-cookie';

import { Box, Heading, Icon, Paragraph, TextButton, UpsellBanner } from '@zillow/constellation-10';
import { IconAIMagicFilled, IconBuildingsFilled } from '@zillow/constellation-icons';

import {
  useRentalManagerUserClaimableListingsWebApiHandler_getClaimableProperties,
  useRentalManagerUserClaimableListingsWebApiHandler_userHasNewPropertiesToClaim,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import { TalkToSalesModal } from '@zg-rentals/rental-revenue-components';

import accessUtils from '../../../../srcLegacy/shared/utils/accessUtils';
import { useScreenSize } from '../../../hooks/useScreenSize';

import { CLAIM_YOUR_LISTING_COOKIES } from '../../../../srcLegacy/shared/constants/CookieConstants';
import {
  emitAfterClaimingContactSalesBannerEvent,
  emitClaimPropertyFromBannerEvent,
  AFTER_CLAIMING_CONTACT_SALES_BANNER_EVENT_ID,
  CLAIM_PROPERTY_FROM_BANNER_EVENT_ID,
} from '../../../analytics/events/claimDashboard';
import PropertiesConstants from '../../../constants/PropertiesConstants';

import { useAuth } from '../../../components/AuthProvider';
import { bannerBoxRecipe } from '../../../components/properties/styles';

const ClaimPathBanners = ({
  hasAnyClaimedListings,
  subNavFilterType,
}: {
  hasAnyClaimedListings: boolean;
  subNavFilterType: string;
}) => {
  const { user } = useAuth();
  const { isScreenDesktop, isScreenMobile } = useScreenSize();

  const { data: { hasNewProperties: hasNewPropertiesToClaim, newPropertiesCount = 0 } = {} } =
    useRentalManagerUserClaimableListingsWebApiHandler_userHasNewPropertiesToClaim({
      enabled: user?.features?.canClaimFreeListings ?? false,
    });
  const { data: { unitProperties: claimableProperties } = {} } =
    useRentalManagerUserClaimableListingsWebApiHandler_getClaimableProperties(
      {},
      {
        enabled: user?.features?.canClaimFreeListings ?? false,
      },
    );

  const isNonAdvertiser = accessUtils.isNonAdvertiser({ user });

  const canAccessClaimYourListingsBanner =
    claimableProperties &&
    subNavFilterType !== PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED &&
    accessUtils.canAccessClaimPath({
      isScreenMobile,
      cookie: CLAIM_YOUR_LISTING_COOKIES.HAS_CLOSED_CLAIM_BANNER,
      hasAnyPropertiesToClaim: claimableProperties.length > 0,
      isNonAdvertiser,
    });

  const canAccessClaimMoreListingsBanner =
    typeof hasNewPropertiesToClaim === 'boolean' &&
    accessUtils.canAccessClaimMorePath({
      isScreenMobile,
      hasAnyClaimedListings,
      hasNewPropertiesToClaim,
      cookie: CLAIM_YOUR_LISTING_COOKIES.HAS_CLOSED_CLAIM_MORE_BANNER,
    });

  const canAccessUpgradeBanner =
    !canAccessClaimMoreListingsBanner &&
    subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED &&
    hasAnyClaimedListings &&
    Cookies.get(CLAIM_YOUR_LISTING_COOKIES.HAS_CLOSED_UPGRADE_BANNER) !== 'true';

  const handleBannerClose = (cookie: string) => {
    Cookies.set(cookie, 'true', {
      expires: CLAIM_YOUR_LISTING_COOKIES.EXPIRATION,
    });
  };

  const handleContactSalesClick = () => {
    emitAfterClaimingContactSalesBannerEvent({ user });
  };

  const renderUpgradeBanner = () => (
    <Box css={bannerBoxRecipe.raw({ isEnterpriseCustomer: true })}>
      {isScreenDesktop ? (
        <UpsellBanner
          data-eventid={AFTER_CLAIMING_CONTACT_SALES_BANNER_EVENT_ID}
          action={
            <TalkToSalesModal
              buttonText="Contact sales"
              onOpen={handleContactSalesClick}
              isFromClaimPathBanner={true}
            />
          }
          header={
            <Heading level={2} textStyle="body-lg-bold">
              Level up your claimed listings.
            </Heading>
          }
          body={<Paragraph>Upgrade to an ad package to get leads from the #1 most visited rentals network.</Paragraph>}
          media={<Icon render={<IconAIMagicFilled />} />}
          onOpenChange={() => handleBannerClose(CLAIM_YOUR_LISTING_COOKIES.HAS_CLOSED_UPGRADE_BANNER)}
        />
      ) : (
        <UpsellBanner.Root
          onOpenChange={() => handleBannerClose(CLAIM_YOUR_LISTING_COOKIES.HAS_CLOSED_UPGRADE_BANNER)}
          data-eventid={AFTER_CLAIMING_CONTACT_SALES_BANNER_EVENT_ID}
        >
          <UpsellBanner.Media css={{ alignSelf: 'start' }}>
            <Icon render={<IconAIMagicFilled />} />
          </UpsellBanner.Media>
          <UpsellBanner.Content css={{ display: 'flex', alignItems: 'center' }}>
            <UpsellBanner.Body>
              <Heading level={2} textStyle="body-lg-bold">
                Level up your claimed listings
              </Heading>
              <Paragraph css={{ display: 'block !important' }}>
                Upgrade to an ad package to get leads from the #1 most visited rentals network
              </Paragraph>
              <TalkToSalesModal
                buttonText="Contact sales"
                onOpen={handleContactSalesClick}
                isFromClaimPathBanner={true}
              />
            </UpsellBanner.Body>
          </UpsellBanner.Content>
          <UpsellBanner.CloseButton />
        </UpsellBanner.Root>
      )}
    </Box>
  );

  const renderClaimYourListingsBanner = () => (
    <Box css={bannerBoxRecipe.raw({ isEnterpriseCustomer: true })}>
      <UpsellBanner
        action={
          <TextButton
            asChild={true}
            onClick={() => emitClaimPropertyFromBannerEvent({ user })}
            data-eventid={CLAIM_PROPERTY_FROM_BANNER_EVENT_ID}
          >
            <a href="/rental-manager/ad-management/how-claiming-works"> Start claiming</a>
          </TextButton>
        }
        header={
          <Heading level={2} textStyle="body-lg-bold">
            {canAccessClaimMoreListingsBanner
              ? `We found ${newPropertiesCount} new ${newPropertiesCount === 1 ? 'property' : 'properties'} to claim.`
              : `Claim and list the properties you manage for free`}
          </Heading>
        }
        body={
          <Paragraph>
            {canAccessClaimMoreListingsBanner
              ? `Review the list and publish your free listing${newPropertiesCount > 1 ? 's' : ''}.`
              : `on the #1 most visited rentals network.`}
          </Paragraph>
        }
        media={<Icon render={<IconBuildingsFilled />} />}
        onOpenChange={() =>
          handleBannerClose(
            canAccessClaimMoreListingsBanner
              ? CLAIM_YOUR_LISTING_COOKIES.HAS_CLOSED_CLAIM_MORE_BANNER
              : CLAIM_YOUR_LISTING_COOKIES.HAS_CLOSED_CLAIM_BANNER,
          )
        }
      />
    </Box>
  );

  return (
    <>
      {(canAccessClaimYourListingsBanner || canAccessClaimMoreListingsBanner) && renderClaimYourListingsBanner()}
      {canAccessUpgradeBanner && renderUpgradeBanner()}
    </>
  );
};

export default ClaimPathBanners;
